import { useState } from 'react';
import { useManagers, useAnalytics, useResetManagerPassword, type TempPassword } from '../api/hooks';
import { Empty, ErrorState, Loading } from '../components/States';

/**
 * Four groups, as asked: assigned, unassigned, pending approval, and needs
 * attention.
 *
 * "Needs attention" is NOT a theft detector, and the section is named honestly
 * for that reason. Stock only leaves through an approved slip, so material that
 * walks off site without one leaves no trace in this database at all — the
 * absence IS the theft, and an absence cannot be aggregated. Labelling a named
 * employee a thief on evidence the system does not have is wrong and a real
 * liability for the customer. So: flag on silence and anomaly, state every
 * reason, accuse nobody. (§2.3)
 *
 * Password reset lives here because there is nowhere else it can live. A manager
 * signs in by phone and has no self-service recovery — an emailed code cannot
 * reach someone whose account has no email. This screen is the only place a
 * person with the authority to help them actually is.
 */
export default function Managers() {
  const { data, isPending, error, refetch } = useManagers('all');
  const { data: a } = useAnalytics(30, null);
  const reset = useResetManagerPassword();

  // Which row is asking "are you sure?", and the one password we were handed.
  const [confirming, setConfirming] = useState<string | null>(null);
  const [issued, setIssued] = useState<TempPassword | null>(null);

  if (isPending) return <Loading what="your managers" />;
  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;
  if (!data?.length) return <Empty title="No managers yet">Invite them from the Android app.</Empty>;

  const pending = data.filter((m) => m.status === 'pending');
  const assigned = data.filter((m) => m.status !== 'pending' && m.assigned);
  const unassigned = data.filter((m) => m.status !== 'pending' && !m.assigned);
  const silentSites = new Set((a?.accountability.stalled_sites ?? []).map((s) => s.site_name));

  function doReset(id: string) {
    setConfirming(null);
    setIssued(null);
    reset.mutate(id, { onSuccess: (d) => setIssued(d) });
  }

  const resetControl = (id: string) => (
    <ResetControl
      confirming={confirming === id}
      busy={reset.isPending && confirming === null && reset.variables === id}
      onAsk={() => { setConfirming(id); setIssued(null); }}
      onCancel={() => setConfirming(null)}
      onConfirm={() => doReset(id)}
    />
  );

  return (
    <>
      <div className="section__head">
        <h1 className="section__title">Managers</h1>
        <p className="section__sub muted">{data.length} in this organisation</p>
      </div>

      {issued && <IssuedPassword issued={issued} onDismiss={() => setIssued(null)} />}
      {reset.isError && (
        <div className="card" style={{ borderColor: 'var(--bad, #f85149)', marginBottom: 16 }}>
          <strong>Could not reset that password.</strong>
          <div className="muted" style={{ fontSize: 13 }}>{(reset.error as Error).message}</div>
        </div>
      )}

      {pending.length > 0 && (
        <Group title="Pending approval" sub="They redeemed an invite code and are waiting on you. Approve them in the Android app.">
          {pending.map((m) => (
            <Row key={m.id} name={m.name} phone={m.phone}
              right={<span className="pill pill--warn">pending</span>}
              actions={resetControl(m.id)} />
          ))}
        </Group>
      )}

      {unassigned.length > 0 && (
        <Group title="Approved, but holding no site" sub="They cannot raise a slip until they are assigned one.">
          {unassigned.map((m) => (
            <Row key={m.id} name={m.name} phone={m.phone}
              right={<span className="pill pill--quiet">no site</span>}
              actions={resetControl(m.id)} />
          ))}
        </Group>
      )}

      <Group title="Assigned" sub={`${assigned.length} manager${assigned.length === 1 ? '' : 's'} on site`}>
        {assigned.map((m) => {
          const quiet = m.site_names.some((s) => silentSites.has(s));
          return (
            <Row
              key={m.id} name={m.name} phone={m.phone}
              sub={m.site_names.join(', ') || m.site_name}
              // Every flag states its own reason. Nothing accuses anyone.
              right={quiet
                ? <span className="pill pill--warn">No activity at their site in 14 days</span>
                : <span className="pill pill--ok">active</span>}
              actions={resetControl(m.id)}
            />
          );
        })}
      </Group>

      <p className="muted" style={{ fontSize: 12, maxWidth: '62ch' }}>
        BhoomiTrack cannot show theft, and does not try to. Stock only leaves through an approved
        slip, so material taken without one leaves no record here at all. These flags are silence
        and anomaly — a reason to make a phone call, never an accusation.
      </p>
    </>
  );
}

/**
 * Shown once, and only once. The server stores the password as a bcrypt hash the
 * instant it is generated, so this panel is genuinely the only place it exists —
 * refreshing loses it and the only remedy is another reset.
 */
function IssuedPassword({ issued, onDismiss }: { issued: TempPassword; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="card" style={{ marginBottom: 16, borderColor: 'var(--gold, #d4a72c)' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        Temporary password for {issued.name}
      </div>
      <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
        Read it to them now{issued.phone ? ` on ${issued.phone}` : ''}. It is not stored anywhere and
        cannot be shown again — if it is lost, reset it a second time. They sign in with their phone
        number and this password, then change it under Settings → Change password.
      </p>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <code className="mono-num" style={{ fontSize: 22, letterSpacing: '.12em', userSelect: 'all' }}>
          {issued.tempPassword}
        </code>
        <button
          type="button"
          className="btn btn--quiet"
          onClick={() => {
            // Clipboard access fails on an insecure origin or when the browser
            // refuses permission; the password is selectable either way.
            navigator.clipboard?.writeText(issued.tempPassword)
              .then(() => setCopied(true))
              .catch(() => setCopied(false));
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button type="button" className="btn btn--quiet" onClick={onDismiss}>Done</button>
      </div>
      <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
        Their existing sessions have all been signed out, so an old login on another phone no
        longer works.
      </p>
    </div>
  );
}

/** Two taps, because the first one signs somebody out of their phone. */
function ResetControl({ confirming, busy, onAsk, onCancel, onConfirm }: {
  confirming: boolean; busy: boolean;
  onAsk: () => void; onCancel: () => void; onConfirm: () => void;
}) {
  if (busy) return <span className="muted" style={{ fontSize: 12 }}>Resetting…</span>;
  if (!confirming) {
    return (
      <button type="button" className="btn btn--quiet" onClick={onAsk}>
        Reset password
      </button>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
      <span className="muted" style={{ fontSize: 12 }}>Sign them out and issue a new password?</span>
      <button type="button" className="btn btn--gold" onClick={onConfirm}>Yes</button>
      <button type="button" className="btn btn--quiet" onClick={onCancel}>No</button>
    </span>
  );
}

function Group({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">{title}</h2>
        {sub && <p className="section__sub muted">{sub}</p>}
      </div>
      <div className="card" style={{ padding: 0 }}>{children}</div>
    </section>
  );
}

function Row({ name, phone, sub, right, actions }: {
  name: string; phone?: string; sub?: string;
  right?: React.ReactNode; actions?: React.ReactNode;
}) {
  return (
    <div className="row">
      <div>
        <div style={{ fontWeight: 600 }}>{name}</div>
        <div className="muted" style={{ fontSize: 12 }}>
          {sub}{sub && phone ? ' · ' : ''}{phone}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {right}
        {actions}
      </div>
    </div>
  );
}
